/**
 * PATH: packages/drafting/src/ir/normalize.ts
 * FILE_ID: DRAFTING-IR-NORMALIZE-001
 * PURPOSE: Pure scale/units/geometry helpers (f(input) -> output).
 * WHY: Scale is the #1 user pain (DRAFTING_STUDIO_SCOPE.md §15). Keeping
 *   the maths in small pure functions makes it unit-testable in
 *   isolation and lets the validator reuse the exact same maths the
 *   renderer uses (no drift between "what we drew" and "what we check").
 */

import type { IrUnit, Point } from './schema'

/** Millimetres per 1 unit of the given unit system. */
const MM_PER_UNIT: Record<IrUnit, number> = {
  mm: 1,
  cm: 10,
  m: 1000,
  in: 25.4,
}

/** unitToMm — convert a value in `unit` to millimetres. */
export function unitToMm(value: number, unit: IrUnit): number {
  return value * (MM_PER_UNIT[unit] ?? 1)
}

/** mmToUnit — convert a millimetre value back to `unit`. */
export function mmToUnit(mm: number, unit: IrUnit): number {
  return mm / (MM_PER_UNIT[unit] ?? 1)
}

export interface ScaleRatio {
  /** drawing side (left of the colon) */
  drawing: number
  /** real-world side (right of the colon) */
  real: number
  /** real units represented per 1 drawing unit (real / drawing) */
  factor: number
}

/**
 * parseScaleRatio — "1:5" -> { drawing:1, real:5, factor:5 }.
 * Returns null for malformed input so callers can surface a clear error
 * rather than silently treating a bad scale as 1:1.
 */
export function parseScaleRatio(ratio: string): ScaleRatio | null {
  const m = /^\s*(\d+(?:\.\d+)?)\s*:\s*(\d+(?:\.\d+)?)\s*$/.exec(ratio ?? '')
  if (!m) return null
  const drawing = Number.parseFloat(m[1])
  const real = Number.parseFloat(m[2])
  if (!Number.isFinite(drawing) || !Number.isFinite(real) || drawing <= 0 || real <= 0) {
    return null
  }
  return { drawing, real, factor: real / drawing }
}

/** Euclidean distance between two points (in drawing units). */
export function distance(a: Point, b: Point): number {
  return Math.hypot(b[0] - a[0], b[1] - a[1])
}

/**
 * realWorldLength — drawing-unit distance -> real-world length for a
 * declared scale. e.g. a 40-drawing-unit segment at 1:5 == 200 real units.
 */
export function realWorldLength(drawingDistance: number, ratio: string): number | null {
  const parsed = parseScaleRatio(ratio)
  if (!parsed) return null
  return drawingDistance * parsed.factor
}

/** snapValue — round a scalar to the nearest grid step (>0). */
export function snapValue(value: number, gridSize: number): number {
  if (!Number.isFinite(gridSize) || gridSize <= 0) return value
  return Math.round(value / gridSize) * gridSize
}

/** snapPoint — round a point to the grid. */
export function snapPoint(p: Point, gridSize: number): Point {
  return [snapValue(p[0], gridSize), snapValue(p[1], gridSize)]
}

/** Round a number to `dp` decimal places (default 2) for clean display. */
export function roundTo(value: number, dp = 2): number {
  const f = 10 ** dp
  return Math.round(value * f) / f
}

/** Format a scale bar label for a given real length + unit. */
export function formatLength(value: number, unit: IrUnit): string {
  return `${roundTo(value, 1)} ${unit}`
}
